import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw, ChevronRight, ChevronDown, ArrowUp, ArrowDown, Upload, FileCheck2 } from 'lucide-react';
import { useBoqSourceFiles } from '../hooks/useBoqSourceFiles';
import { BrioCard, BrioEmptyState } from '../../components/BrioComponents';
import { ToolbarIcon } from '../components/ToolbarIcon';
import { StickyScrollBox } from '../components/StickyScrollbar';

// ============================================================================
// Source files — every workbook or PDF the library was built from, and whether
// each bidder's rates in it read as Estimate or Tender.
//
// Estimate = what the work should cost, priced by a consultant or the in-house
// QS. Tender = what a contractor offered to do it for. The choice is per
// bidder, because one comparison workbook can hold both (TG: the JADE
// reference estimate beside six contractors' bids). A file with several bidders
// gets a row per bidder plus a file row that sets them all at once.
//
// Ticking a box MOVES that bidder's figures into the chosen column (see
// boq.set_rate_basis), so every page that reads rates agrees with it. The two
// boxes act as a switch: a rate is always one or the other, so a ticked box is
// changed by ticking its partner, never by unticking it.
// ============================================================================

const cell = { padding: '0.4rem 0.55rem', verticalAlign: 'middle' };
const numCell = { ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
const boxCell = { ...cell, textAlign: 'center', width: 84 };
const muted = { color: 'var(--brio-text-muted)' };

const fmtDate = (iso) => (iso
  ? new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
  : '—');

// Box state for a set of rates: checked when they are all on that basis,
// indeterminate when they are split between the two.
function boxState(entity, basis) {
  const on = basis === 'estimate' ? entity.estimateCount : entity.tenderCount;
  const other = basis === 'estimate' ? entity.tenderCount : entity.estimateCount;
  return { checked: on > 0 && other === 0, mixed: on > 0 && other > 0 };
}

function BasisBox({ entity, basis, disabled, busy, onPick, label }) {
  const ref = useRef(null);
  const { checked, mixed } = boxState(entity, basis);
  useEffect(() => { if (ref.current) ref.current.indeterminate = mixed; }, [mixed]);
  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label={label}
      checked={checked}
      disabled={disabled || busy}
      // A ticked box stays ticked: the way out is ticking the other basis.
      onChange={() => { if (!checked) onPick(basis); }}
      style={{ width: 16, height: 16, cursor: disabled ? 'not-allowed' : 'pointer', accentColor: 'var(--brio-gold, #9F902A)' }}
    />
  );
}

// Sort keys per column. Priced by on a file with several bidders sorts on its
// first bidder, so the file lands among the names it actually contains.
const pricedBy = (f) => (f.bidders.length > 1
  ? (f.bidders[0].contractor || '')
  : (f.bidders[0]?.contractor || f.consultant || ''));
// Default order: SD budget / costing workbooks, then files whose rates are
// mostly Estimate, then mostly Tender, then files with no rates yet. Grouped
// on the file's own stage and majority basis, so a tender that also feeds a
// few SD area rates (TG) stays with the tenders.
const stageGroup = (f) => {
  if (f.isSdSource) return 0;
  if (f.rateCount === 0) return 3;
  return f.estimateCount > f.tenderCount ? 1 : 2;
};
const SORT_KEYS = {
  group: (f) => stageGroup(f),
  filename: (f) => (f.filename || '').toLowerCase(),
  date: (f) => f.documentDate || '',
  pricedBy: (f) => pricedBy(f).toLowerCase(),
  versions: (f) => f.versionsCreated,
  rates: (f) => f.rateCount,
  // How many of the file's rates sit on each basis, so ascending puts the
  // files with none on that basis first.
  estimate: (f) => f.estimateCount,
  tender: (f) => f.tenderCount,
  // SD sources first (by how many area rates they feed), then tender files
  // that feed SD rates, then the rest.
  sd: (f) => (usedForSd(f) ? 100000 : 0) + f.sdRateCount,
  stored: (f) => (f.storagePath ? 1 : 0),
};

// SD BOQ column: a read-only tick for every file used for SD rates, whether a
// budget / costing workbook or a tender some area rates were worked out from.
// It records what the file is, so there is nothing here to change.
const usedForSd = (f) => f.isSdSource || f.sdRateCount > 0;
function SdBox({ file }) {
  const on = usedForSd(file);
  const tip = !on ? 'Not used for SD rates'
    : file.sdRateCount ? `Used for SD rates · ${file.sdRateCount} area rate${file.sdRateCount === 1 ? '' : 's'}`
    : 'SD source · no area rates worked out yet';
  return (
    <input
      type="checkbox"
      checked={on}
      readOnly
      tabIndex={-1}
      aria-label={`${file.filename}: used for SD rates`}
      title={tip}
      onClick={(e) => e.preventDefault()}
      style={{ width: 16, height: 16, cursor: 'default', accentColor: 'var(--brio-gold, #9F902A)' }}
    />
  );
}

// File column: whether the file itself is stored, which is what the Rates
// page's eye icon opens. Curators upload or replace it from here.
function FileCell({ file, canUpload, busy, onUpload }) {
  const inputRef = useRef(null);
  const stored = !!file.storagePath;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {stored
        ? <span title="Stored · its rates can show where they came from" style={{ display: 'inline-flex' }}><FileCheck2 size={16} style={{ color: 'var(--brio-gold, #9F902A)' }} aria-label="Stored" /></span>
        : <span style={{ ...muted, fontSize: '0.76rem' }}>Not uploaded</span>}
      {canUpload && (
        <>
          <button type="button" disabled={busy}
            onClick={() => inputRef.current?.click()}
            title={stored ? 'Replace the stored file' : 'Upload this file so its rates can show where they came from'}
            aria-label={`${stored ? 'Replace' : 'Upload'} ${file.filename}`}
            style={{ display: 'inline-flex', alignItems: 'center', background: 'none', border: 'none', padding: 0, cursor: busy ? 'wait' : 'pointer', color: 'var(--brio-text-muted)' }}>
            <Upload size={15} />
          </button>
          <input ref={inputRef} type="file" hidden
            accept=".xlsx,.xlsm,.xls,.pdf"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onUpload(file, f); }} />
        </>
      )}
    </span>
  );
}

function SourceFilesPage({ onRatesChanged }) {
  const src = useBoqSourceFiles();
  // Bidder rows show by default; this holds the files someone has folded.
  const [closed, setClosed] = useState(() => new Set());
  const [sort, setSort] = useState({ field: 'group', dir: 'asc' });
  const [busyKey, setBusyKey] = useState(null);
  const [message, setMessage] = useState(null);

  const upload = async (file, picked) => {
    // A different name is allowed (a re-saved copy), but say so first.
    if (picked.name !== file.filename) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`The chosen file is named "${picked.name}", not "${file.filename}". Store it as ${file.filename}?`)) return;
    }
    setBusyKey(`upload|${file.id}`);
    setMessage(null);
    const res = await src.uploadFile({ documentId: file.id, filename: file.filename, file: picked });
    setBusyKey(null);
    setMessage(res.success
      ? { error: false, text: `${file.filename} stored.` }
      : { error: true, text: res.error });
  };

  const toggleOpen = (id) => setClosed((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const pick = async ({ file, bidder = null, basis }) => {
    const allBidders = bidder === null;
    const entity = allBidders ? file : bidder;
    const toMove = basis === 'tender' ? entity.estimateCount : entity.tenderCount;
    if (toMove === 0) return;
    const who = allBidders
      ? (file.bidders.length > 1 ? `all ${file.bidders.length} bidders in ${file.filename}` : file.filename)
      : `${bidder.contractor || 'No bidder named'} in ${file.filename}`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Move ${toMove} rate${toMove === 1 ? '' : 's'} from ${who} to ${basis === 'tender' ? 'Tender' : 'Estimate'}?`)) return;
    const key = `${file.id}|${allBidders ? '*' : bidder.contractor}`;
    setBusyKey(key);
    setMessage(null);
    const res = await src.setBasis({ documentId: file.id, contractor: allBidders ? null : bidder.contractor, allBidders, basis });
    setBusyKey(null);
    if (!res.success) { setMessage({ error: true, text: res.error }); return; }
    setMessage({ error: false, text: `${res.moved} rate${res.moved === 1 ? '' : 's'} moved to ${basis === 'tender' ? 'Tender' : 'Estimate'}.` });
    if (onRatesChanged) onRatesChanged();
  };

  const files = useMemo(() => {
    const key = SORT_KEYS[sort.field];
    const sign = sort.dir === 'asc' ? 1 : -1;
    return [...src.files].sort((a, b) => {
      const x = key(a); const y = key(b);
      if (x < y) return -sign;
      if (x > y) return sign;
      return (a.filename || '').localeCompare(b.filename || '');
    });
  }, [src.files, sort]);

  const toggleSort = (field) => setSort((prev) => (prev.field === field
    ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
    : { field, dir: 'asc' }));

  // A plain render helper, not a component, so React does not remount the
  // headers on every render.
  const sortHead = (field, label, numeric = false, style = null) => {
    const active = sort.field === field;
    return (
      <th key={field} style={style || (numeric ? numCell : cell)} aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
        <button type="button" onClick={() => toggleSort(field)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: active ? 'var(--brio-text, #222)' : 'inherit', fontWeight: active ? 600 : 'inherit' }}>
          {label}
          {active && (sort.dir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
        </button>
      </th>
    );
  };

  const disabled = !src.canClassify;

  const renderBoxes = (file, bidder, key) => {
    const entity = bidder || file;
    if (entity.rateCount === 0) {
      // An SD workbook feeds area rates, not item rates, so it leaves these
      // blank rather than reading "No rates"; its SD BOQ tick says what it is.
      return <td colSpan={2} style={{ ...cell, ...muted, textAlign: 'center', fontSize: '0.78rem' }}>
        {file.isSdSource ? null : 'No rates'}
      </td>;
    }
    const busy = busyKey === key;
    const name = bidder ? (bidder.contractor || file.filename) : file.filename;
    return (
      <>
        <td style={boxCell}>
          <BasisBox entity={entity} basis="estimate" disabled={disabled} busy={busy} label={`${name}: Estimate`}
            onPick={(basis) => pick({ file, bidder, basis })} />
        </td>
        <td style={boxCell}>
          <BasisBox entity={entity} basis="tender" disabled={disabled} busy={busy} label={`${name}: Tender`}
            onPick={(basis) => pick({ file, bidder, basis })} />
        </td>
      </>
    );
  };

  return (
    <div>
      <div style={{ marginBottom: '1rem', fontSize: '0.85rem', ...muted, maxWidth: 820 }}>
        Every file the library was built from. <strong>Estimate</strong> is what the work should cost, priced by a
        consultant or the in-house QS. <strong>Tender</strong> is what a contractor offered to do it for. A file with
        several bidders lists each one, so a comparison holding a reference estimate beside contractor bids can be
        set bidder by bidder; the file row sets them all at once. Ticking a box moves that bidder's rates into that
        column on every page.
        {disabled && !src.loading && (
          <div style={{ marginTop: '0.4rem' }}>Only admins and library curators can change these.</div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', gap: '1rem' }}>
        <div style={{ fontSize: '0.82rem', color: message?.error ? '#a94442' : 'var(--brio-text-muted)' }}>
          {message?.text || ''}
        </div>
        <ToolbarIcon onClick={src.refresh} tooltip="Refresh"><RefreshCw size={15} /></ToolbarIcon>
      </div>

      {src.error && <div style={{ color: '#a94442', marginBottom: '0.75rem' }}>{src.error}</div>}

      {src.loading ? (
        <BrioCard><div style={{ ...muted, padding: '0.5rem' }}>Loading source files…</div></BrioCard>
      ) : src.files.length === 0 ? (
        <BrioCard><BrioEmptyState>No source files recorded yet.</BrioEmptyState></BrioCard>
      ) : (
        <BrioCard>
          <StickyScrollBox style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: 900, borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '2px solid var(--brio-border, #ddd)', ...muted }}>
                  {sortHead('filename', 'Source file')}
                  {sortHead('date', 'Date')}
                  {sortHead('pricedBy', 'Priced by')}
                  {sortHead('versions', 'Items created', true)}
                  {sortHead('rates', 'Rates', true)}
                  {sortHead('estimate', 'Estimate', false, boxCell)}
                  {sortHead('tender', 'Tender', false, boxCell)}
                  {sortHead('sd', 'SD BOQ', false, boxCell)}
                  {sortHead('stored', 'File', false, { ...cell, width: 110 })}
                </tr>
              </thead>
              <tbody>
                {files.map((file) => {
                  const multi = file.bidders.length > 1;
                  const single = file.bidders.length === 1 ? file.bidders[0] : null;
                  const isOpen = !closed.has(file.id);
                  return (
                    <React.Fragment key={file.id}>
                      <tr style={{ borderBottom: '1px solid var(--brio-border-subtle, #eee)' }}>
                        <td style={{ ...cell, fontWeight: 600, wordBreak: 'break-all' }}>
                          {multi ? (
                            <button type="button" onClick={() => toggleOpen(file.id)}
                              aria-expanded={isOpen}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit', textAlign: 'left' }}>
                              {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                              {file.filename}
                            </button>
                          ) : file.filename}
                        </td>
                        <td style={{ ...cell, whiteSpace: 'nowrap' }}>{fmtDate(file.documentDate)}</td>
                        <td style={cell}>
                          {multi
                            ? <span style={muted}>{file.bidders.length} bidders</span>
                            : (single?.contractor || file.consultant || <span style={muted}>—</span>)}
                        </td>
                        <td style={numCell}>{file.versionsCreated || <span style={muted}>—</span>}</td>
                        <td style={numCell}>{file.rateCount || <span style={muted}>—</span>}</td>
                        {renderBoxes(file, single, `${file.id}|${single ? single.contractor : '*'}`)}
                        <td style={boxCell}><SdBox file={file} /></td>
                        <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                          <FileCell file={file} canUpload={src.canClassify} busy={busyKey === `upload|${file.id}`} onUpload={upload} />
                        </td>
                      </tr>
                      {multi && isOpen && file.bidders.map((b) => (
                        <tr key={`${file.id}|${b.contractor}`} style={{ borderBottom: '1px solid var(--brio-border-subtle, #eee)', background: 'var(--brio-surface-muted, #f7f7f5)' }}>
                          <td style={cell} />
                          <td style={cell} />
                          <td style={cell}>{b.contractor || <span style={muted}>No bidder named</span>}</td>
                          <td style={cell} />
                          <td style={numCell}>{b.rateCount}</td>
                          {renderBoxes(file, b, `${file.id}|${b.contractor}`)}
                          <td style={cell} />
                          <td style={cell} />
                        </tr>
                      ))}
                      {file.bothCount > 0 && (
                        <tr>
                          <td colSpan={9} style={{ ...cell, fontSize: '0.76rem', color: '#a94442' }}>
                            {file.bothCount} rate{file.bothCount === 1 ? ' has' : 's have'} both an estimate and a tender figure
                            and {file.bothCount === 1 ? 'is' : 'are'} left as {file.bothCount === 1 ? 'it is' : 'they are'}.
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </StickyScrollBox>
        </BrioCard>
      )}
    </div>
  );
}

export default SourceFilesPage;
